import { useCallback, useEffect, useState } from "react";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import { SudoButton } from "../sudokit/components/SudoButton";

interface SeriesProject {
  id: string;
  title: string;
  order: number;
}

interface SeriesItem {
  id: string;
  title: string;
  collapseTimeline: boolean;
  projectCount: number;
  projects: SeriesProject[];
}

interface SeriesProps {
  user?: { uid: string } | null;
}

function sanitizeFilename(value: string) {
  return (value || "series").replace(/[^a-zA-Z0-9]/g, "_");
}

export default function Series({ user }: SeriesProps) {
  const [items, setItems] = useState<SeriesItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [exportingSeriesId, setExportingSeriesId] = useState<string | null>(null);
  const { setToast } = useToast();

  const loadSeries = useCallback(async () => {
    if (!user?.uid) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const response: { series?: SeriesItem[]; success?: boolean; error?: string; message?: string } =
        await Auth.fetchWithIdToken(`/api/admin/users/${user.uid}/series`, {});
      if (response?.success === false) {
        throw new Error(response.error || response.message || "Failed to load series");
      }
      setItems(response.series || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load series";
      setToast(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [setToast, user?.uid]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const handleExport = useCallback(
    async (series: SeriesItem) => {
      if (exportingSeriesId) return;
      setExportingSeriesId(series.id);
      try {
        const blob = await Auth.fetchFileWithIdToken("/api/admin/series/export", {
          seriesId: series.id,
        });
        if ((blob.type || "").includes("application/json")) {
          const jsonText = await blob.text();
          try {
            const payload = JSON.parse(jsonText);
            throw new Error(payload?.error || payload?.message || "Series export returned JSON instead of a ZIP file");
          } catch (parseError) {
            if (parseError instanceof Error) {
              throw parseError;
            }
            throw new Error("Series export returned JSON instead of a ZIP file");
          }
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `${sanitizeFilename(series.title)}_${series.id}_${timestamp}.zip`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setToast(`✅ Exported series "${series.title || "Untitled Series"}"`);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Series export failed";
        setToast(`❌ Export failed: ${message}`);
      } finally {
        setExportingSeriesId(null);
      }
    },
    [exportingSeriesId, setToast]
  );

  const handleImport = useCallback(async () => {
    if (!selectedFile || !user?.uid || isImporting) return;
    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("userId", user.uid);

      const response = await Auth.postFilesWithIdToken("/api/admin/series/import", {}, formData);
      if (!response?.success) {
        throw new Error(response?.error || "Series import failed");
      }

      setToast(`✅ Imported series (${response.importedProjectCount || 0} projects)`);
      setSelectedFile(null);
      const input = document.getElementById("series-import-file") as HTMLInputElement | null;
      if (input) input.value = "";
      await loadSeries();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Series import failed";
      setToast(`❌ Import failed: ${message}`);
    } finally {
      setIsImporting(false);
    }
  }, [isImporting, loadSeries, selectedFile, setToast, user?.uid]);

  return (
    <div className="p-5">
      <div className="mb-4 flex items-center gap-3">
        <SudoButton
          variant="secondary"
          size="small"
          className="h-8 px-3 text-xs"
          onClick={() => document.getElementById("series-import-file")?.click()}
        >
          📥 Select Series ZIP
        </SudoButton>
        <input
          id="series-import-file"
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
        />

        {selectedFile && (
          <>
            <span className="text-xs text-gray-600 truncate max-w-[320px]">{selectedFile.name}</span>
            <SudoButton
              variant="primary"
              size="small"
              className="h-8 px-3 text-xs"
              onClick={handleImport}
              disabled={isImporting}
            >
              {isImporting ? "⏳ Importing..." : "📥 Import Series"}
            </SudoButton>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading series...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500">No series found for this user.</div>
      ) : (
        <div className="space-y-3">
          {items.map((series) => (
            <div key={series.id} className="border border-gray-200 rounded-lg bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{series.title || "Untitled Series"}</div>
                  <div className="text-xs text-gray-500 font-mono">{series.id}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {series.projectCount} project{series.projectCount === 1 ? "" : "s"}
                    {series.collapseTimeline ? " • Timeline collapsed" : ""}
                  </div>
                </div>
                <SudoButton
                  variant="secondary"
                  size="small"
                  className="text-xs px-3 py-1"
                  onClick={() => handleExport(series)}
                  disabled={!!exportingSeriesId}
                >
                  {exportingSeriesId === series.id ? "⏳ Exporting..." : "📦 Export Series"}
                </SudoButton>
              </div>

              {series.projects?.length > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <div className="text-xs font-medium text-gray-500 mb-1">Projects</div>
                  <div className="space-y-1">
                    {series.projects.map((project) => (
                      <div key={project.id} className="text-xs text-gray-700">
                        #{project.order}: {project.title} <span className="font-mono text-gray-400">({project.id})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
